import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  companyUserApiClient,
  CompanyUserCreateErrorResponse,
} from '@src/api/company/auth/companyUserApiClient';
import { companyRoleApiClient } from '@src/api/company/common/companyRole/companyRoleApiClient';
import { useToast } from '@src/features/toast/context/toastContext';
import { useScrollToError } from '@src/hooks/useScrollToError';
import TOAST_MESSAGE from '@src/modules/constants/toastMessage';
import { COMPANY_URL } from '@src/routes/company';
import Button from '@src/ui/elements/button/Button';
import TextLink from '@src/ui/elements/button/TextLink';
import ErrorMessage from '@src/ui/elements/form/ErrorMessage';
import Input from '@src/ui/elements/form/Input';
import Select, { SelectorItem } from '@src/ui/elements/form/Select';
import Title from '@src/ui/elements/typography/Title.tsx';
import { firstError, hasError } from '@src/utils/errorMessageUtils';
import logger from '@src/utils/logger';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

type FormValues = {
  familyName: string;
  firstName: string;
  familyNameKana: string;
  firstNameKana: string;
  email: string;
  companyRoleId: string;
};

type RoleSelector = SelectorItem[];

const CompanyUserCreateForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [errors, setErrors] = useState<CompanyUserCreateErrorResponse>();
  const [roleSelector, setRoleSelector] = useState<RoleSelector>();
  const [formValues, setFormValues] = useState<FormValues>({
    familyName: '',
    firstName: '',
    familyNameKana: '',
    firstNameKana: '',
    email: '',
    companyRoleId: '',
  });
  const temporaryUserId = Number(searchParams.get('id'));

  useEffect(() => {
    logger.log('useEffect');
    const fetchCompanyRoles = async () => {
      const res = await companyRoleApiClient.index();
      handleApiResponse(res, (data) => {
        setRoleSelector(
          data.map((role) => {
            return {
              id: role.id,
              name: role.name,
            };
          }),
        );
      });
    };

    const loadTemporaryUser = async (id: number) => {
      const res = await companyUserApiClient.showTemporary(id);
      handleApiResponse(res, (data) => {
        setFormValues({
          familyName: data.family_name,
          firstName: data.first_name,
          familyNameKana: data.family_name_kana,
          firstNameKana: data.first_name_kana,
          email: data.email,
          companyRoleId: '',
        });
      });
    };

    fetchCompanyRoles();

    if (temporaryUserId) {
      loadTemporaryUser(temporaryUserId);
    }
  }, [temporaryUserId]);

  const submit = async () => {
    if (submitted) {
      return;
    }
    setErrors(undefined);
    setSubmitted(true);
    const res = await companyUserApiClient.create({
      company_user: {
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        email: formValues.email,
        company_role_id: formValues.companyRoleId,
      },
    });

    handleApiResponse(
      res,
      (data) => {
        logger.log(data);
        toast(TOAST_MESSAGE.REGISTER);
        navigate(COMPANY_URL.COMPANY_USERS);
      },
      (errors: CompanyUserCreateErrorResponse) => {
        logger.error(errors);
        setErrors(errors);
        setSubmitted(false);
      },
    );
  };

  const handleClickCancel = () => {
    navigate(COMPANY_URL.COMPANY_USERS);
  };

  const fieldRefs = useScrollToError(errors ?? {});

  return (
    <main className='px-6'>
      <div>
        <div className='flex flex-col'>
          <div className='mt-12 flex justify-between gap-4'>
            <div className='pt-2'>
              <Title>ユーザーの作成</Title>
            </div>
          </div>
        </div>
        <div className='mx-auto max-w-[800px] py-[60px]'>
          <form
            className='mt-10 flex flex-col gap-[26px]'
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className=''>氏名</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex gap-4'>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['company_user.family_name'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='山田'
                    value={formValues.familyName}
                    onChange={(e) => setFormValues({ ...formValues, familyName: e.target.value })}
                    error={hasError(errors?.company_user.family_name)}
                  />
                  <ErrorMessage>{firstError(errors?.company_user.family_name)}</ErrorMessage>
                </div>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['company_user.first_name'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='太郎'
                    value={formValues.firstName}
                    onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
                    error={hasError(errors?.company_user.first_name)}
                  />
                  <ErrorMessage>{firstError(errors?.company_user.first_name)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className=''>氏名（フリガナ）</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div className='flex gap-4'>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['company_user.family_name_kana'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='ヤマダ'
                    value={formValues.familyNameKana}
                    onChange={(e) =>
                      setFormValues({ ...formValues, familyNameKana: e.target.value })
                    }
                    error={hasError(errors?.company_user.family_name_kana)}
                  />
                  <ErrorMessage>{firstError(errors?.company_user.family_name_kana)}</ErrorMessage>
                </div>
                <div
                  className='flex w-full flex-col'
                  ref={(el) => (fieldRefs.current['company_user.first_name_kana'] = el)}
                >
                  <Input
                    size='m'
                    type='text'
                    placeholder='タロウ'
                    value={formValues.firstNameKana}
                    onChange={(e) =>
                      setFormValues({ ...formValues, firstNameKana: e.target.value })
                    }
                    error={hasError(errors?.company_user.first_name_kana)}
                  />
                  <ErrorMessage>{firstError(errors?.company_user.first_name_kana)}</ErrorMessage>
                </div>
              </div>
            </div>
            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className=''>メールアドレス</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
              </div>
              <div
                className='flex'
                ref={(el) => (fieldRefs.current['company_user.email'] = el)}
              >
                <Input
                  size='m'
                  type='text'
                  placeholder='sample@sample.co.jp'
                  value={formValues.email}
                  onChange={(e) => setFormValues({ ...formValues, email: e.target.value })}
                  error={hasError(errors?.company_user.email)}
                />
              </div>
              <ErrorMessage>{firstError(errors?.company_user.email)}</ErrorMessage>
            </div>

            <div className='flex flex-col gap-[6px]'>
              <div className='flex items-center gap-3'>
                <label className=''>権限</label>
                <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                  必須
                </div>
                <span className='text-xs'>
                  詳細は
                  <TextLink
                    text='こちら'
                    onClick={() =>
                      window.open(
                        `${import.meta.env.VITE_HELP_BASE_URL}${import.meta.env.VITE_COMPANY_ROLE_GUIDE_URL}`,
                        '_blank',
                      )
                    }
                  />
                </span>
              </div>
              <div
                ref={(el) => (fieldRefs.current['company_user.company_role_id'] = el)}
                className='flex w-full flex-col'
              >
                {roleSelector && (
                  <Select
                    items={roleSelector}
                    size='m'
                    onSelect={(value) => setFormValues({ ...formValues, companyRoleId: value })}
                    error={hasError(errors?.company_user.company_role_id)}
                  />
                )}
              </div>
              <ErrorMessage>{firstError(errors?.company_user.company_role_id)}</ErrorMessage>
            </div>
            <div className='mt-auto flex justify-center gap-5'>
              <div className='w-[172px]'>
                <Button
                  variant='secondary'
                  fullRounded
                  fullWidth
                  outline
                  onClick={handleClickCancel}
                >
                  キャンセル
                </Button>
              </div>
              <div className='w-[172px]'>
                <Button
                  fullRounded
                  fullWidth
                  outline
                  onClick={submit}
                >
                  作成する
                </Button>
              </div>
            </div>
            <p className='grid place-items-center text-sm'>
              入力したメールアドレス宛に認証メールが送信されます
            </p>
          </form>
        </div>
      </div>
    </main>
  );
};

export default CompanyUserCreateForm;
